import { useColorScheme, useTheme } from '@mui/material/styles';
import type { AgCartesianChartOptions } from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import type { AgCartesianAxesOptions } from 'ag-charts-types';
import { cloneDeep, startCase } from 'lodash';
import { useLocation } from 'react-router';

import {
  getAgChartsTheme,
  getSuffix,
  noDataRenderer
} from '@/components/charts/charts.utils';
import {
  formatLargeNumber,
  hasDataGreaterThanZero
} from '@/utils/Common.utils';

import { useChartLocationCheck } from '../charts.hooks';
import useStyles from './VerticalBarChart.styles';
import type { VerticalBarChartProps } from './VerticalBarCharts.types';

const VerticalBarChart = (props: VerticalBarChartProps) => {
  const {
    data,
    series,
    height,
    suppressExponentialAxis,
    suppressDoubleAxis,
    suppressLegend,
    suppressYAxis,
    suppressXAxis,
    suppressCrosshairLine,
    doubleAxis,
    outlined
  } = props;
  const { mode: themeMode } = useColorScheme();
  const theme = useTheme();

  const { pathname } = useLocation();
  const { classes } = useStyles({ outlined });
  const onReportsPage = useChartLocationCheck();
  const hasVisibleData = hasDataGreaterThanZero(data || []);

  const numFormatter = new Intl.NumberFormat('en-US');

  let dataDetails: { [key: string]: string } | null = null;
  if (data?.length && series?.length) {
    const key1 = series[0]?.yKey || '';
    const count1 = data.reduce((sum, item) => sum + (item[key1] || 0), 0);
    const key2 = series[1]?.yKey || '';
    const count2 = data.reduce((sum, item) => sum + (item[key2] || 0), 0);
    dataDetails = {
      [key1]: numFormatter.format(count1),
      [key2]: numFormatter.format(count2)
    };
  }

  const formattedAxes = (): AgCartesianAxesOptions => {
    if (!data?.length || !series?.length) {
      return {
        x: {
          type: 'category',
          position: 'bottom',
          tick: {
            enabled: false
          },
          line: {
            enabled: false
          }
        },
        y: {
          type: 'number',
          position: 'left',
          title: {
            enabled: false
          },
          line: {
            enabled: false
          },
          gridLine: {
            style: [
              {
                stroke: pathname.includes('reports')
                  ? 'lightgray'
                  : theme.vars.palette.border.light
              }
            ]
          }
        }
      };
    }
    const axes = series.map((s) => {
      return s.yKey;
    });

    const dataCopy = cloneDeep(data);
    const xKey = series[0]?.xKey || 'date';
    const key1 = series[0]?.yKey || '';
    const key2 = series[1]?.yKey || '';
    const max1 = Math.max(...dataCopy.map((d) => d[key1]));
    const max2 = Math.max(...dataCopy.map((d) => d[key2]));
    const exponent1 = Math.floor(Math.log10(max1));
    const exponent2 = Math.floor(Math.log10(max2));

    const suffix1 = getSuffix(exponent1);
    const suffix2 = getSuffix(exponent2);

    const xAxisType = xKey === 'date' ? 'unit-time' : 'category';

    if (
      (axes.length < 2 ||
        exponent1 === exponent2 ||
        (suffix1 === '' && suffix2 === '') ||
        suppressDoubleAxis) &&
      !doubleAxis
    ) {
      const xAxis =
        xAxisType === 'unit-time'
          ? {
              type: 'unit-time' as const,
              position: 'bottom' as const,
              label: {
                enabled: !suppressXAxis,
                format: '%b %d',
                color: onReportsPage ? 'black' : undefined
              },
              tick: {
                enabled: !suppressXAxis
              },
              line: {
                enabled: hasVisibleData
              }
            }
          : {
              type: 'category' as const,
              position: 'bottom' as const,
              label: {
                enabled: !suppressXAxis,
                color: onReportsPage ? 'black' : undefined
              },
              tick: {
                enabled: !suppressXAxis
              },
              line: {
                enabled: hasVisibleData
              }
            };

      return {
        x: xAxis,
        y: {
          type: 'number',
          position: 'left',
          label: {
            enabled: !suppressYAxis,
            color: onReportsPage ? 'black' : undefined,
            formatter: (params: { value: number }) => {
              if (suppressExponentialAxis) {
                return numFormatter.format(params.value);
              }
              return formatLargeNumber(params.value);
            }
          },
          crosshair: {
            enabled: !suppressCrosshairLine
          },
          title: {
            enabled:
              hasVisibleData && exponent1 > 2 && !suppressExponentialAxis,
            text:
              exponent1 > 2
                ? `${series[0]?.yName || ''} (${suffix1})`
                : series[0]?.yName || '',
            color:
              series[0]?.fill && typeof series[0].fill === 'string'
                ? series[0].fill
                : undefined
          },
          gridLine: {
            enabled: !suppressYAxis,
            style: [
              {
                stroke: pathname.includes('reports')
                  ? 'lightgray'
                  : theme.vars.palette.border.light
              }
            ]
          },
          line: {
            enabled: hasVisibleData && !suppressYAxis
          },
          min: 0
        }
      };
    }

    const xAxis =
      xAxisType === 'unit-time'
        ? {
            type: 'unit-time' as const,
            position: 'bottom' as const,
            label: { format: '%b %d' },
            tick: { enabled: true },
            line: { enabled: hasVisibleData }
          }
        : {
            type: 'category' as const,
            position: 'bottom' as const,
            tick: { enabled: true },
            line: { enabled: hasVisibleData }
          };

    return {
      x: xAxis,
      y: {
        type: 'number',
        position: 'left',
        title: {
          enabled: hasVisibleData,
          text: series[0]?.yName || '',
          color:
            series[0]?.fill && typeof series[0].fill === 'string'
              ? series[0].fill
              : undefined
        },
        crosshair: {
          enabled: !suppressCrosshairLine
        },
        label: {
          enabled: !suppressYAxis,
          color: onReportsPage ? 'black' : undefined,
          formatter: (params: { value: number }) => {
            if (suppressExponentialAxis) {
              return numFormatter.format(params.value);
            }
            return formatLargeNumber(params.value);
          }
        },
        line: {
          enabled: hasVisibleData && !suppressYAxis
        },
        min: 0,
        max: dataDetails?.[key1] === '0' ? 1 : undefined
      },
      ySecondary: {
        type: 'number',
        position: 'right',
        title: {
          enabled: hasVisibleData,
          text: series[1]?.yName || '',
          color:
            series[1]?.fill && typeof series[1].fill === 'string'
              ? series[1]?.fill
              : undefined
        },
        crosshair: {
          enabled: !suppressCrosshairLine
        },
        label: {
          enabled: !suppressYAxis,
          color: onReportsPage ? 'black' : undefined,
          formatter: (params: { value: number }) => {
            if (suppressExponentialAxis) {
              return numFormatter.format(params.value);
            }
            return formatLargeNumber(params.value);
          }
        },
        line: {
          enabled: hasVisibleData && !suppressYAxis
        },
        min: 0,
        max: dataDetails?.[key2] === '0' ? 1 : undefined
      }
    };
  };

  const showLegend = suppressLegend
    ? false
    : series.length > 1 && hasVisibleData;

  const options: AgCartesianChartOptions = {
    data: hasVisibleData ? data : [],
    series,
    background: {
      fill: 'transparent'
    },
    legend: {
      enabled: showLegend,
      spacing: 12,
      item: {
        label: {
          color: onReportsPage ? 'black' : undefined,
          formatter: ({ itemId }) => {
            return `${startCase(itemId)} - ${dataDetails?.[itemId]}`;
          }
        }
      }
    },
    padding: {
      bottom: 0,
      left: 8
    },
    theme: getAgChartsTheme(themeMode),
    axes: formattedAxes(),
    height,
    overlays: {
      noData: {
        renderer: () => noDataRenderer()
      }
    }
  };

  return <AgCharts className={classes.verticalBarChart} options={options} />;
};

export default VerticalBarChart;
